import { useEffect, useRef, useState } from "react";
import type { Session, SessionMessage } from "../lib/session";
import type { BabyAIMoshPitResult } from "../lib/babyai-client";
import { useSessionStore } from "../store/sessionStore";
import { useAuthStore } from "../store/authStore";
import { MoshPitChoices } from "./MoshPitChoices";
import { MD } from "./Markdown";

interface Props {
  session: Session;
  pendingStream?: string;
  pendingMoshPit?: BabyAIMoshPitResult;
  onMoshPitAccept: (provider: string, content: string) => void;
  busy: boolean;
}

export function ChatThread({
  session,
  pendingStream,
  pendingMoshPit,
  onMoshPitAccept,
  busy,
}: Props) {
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [session.messages.length, pendingStream, pendingMoshPit, busy]);

  if (session.messages.length === 0 && !busy && !pendingMoshPit && !pendingStream) {
    return (
      <div className="thread">
        <div className="empty">
          <h2>{session.title}</h2>
          <p>This is a fresh channel. Type a message below to start.</p>
          <p style={{ marginTop: 12, fontSize: 11 }}>
            Mosh Pit is {session.mosh_pit_enabled ? "on" : "off"} — 3 BabyAI
            models will weigh in per turn and you pick the winner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="thread">
      {session.messages.map((m) => (
        <Message key={m.id} message={m} channelId={session.id} />
      ))}

      {pendingMoshPit && (
        <MoshPitChoices result={pendingMoshPit} onAccept={onMoshPitAccept} />
      )}

      {pendingStream !== undefined && !pendingMoshPit && (
        <div className="msg assistant">
          <div className="msg-meta">
            <span className="author">assistant</span>
          </div>
          <div className="msg-body">
            {pendingStream
              ? <MD>{pendingStream}</MD>
              : <span className="thinking">thinking</span>}
          </div>
        </div>
      )}

      {busy && pendingStream === undefined && !pendingMoshPit && (
        <div className="msg assistant">
          <div className="msg-meta"><span className="author">assistant</span></div>
          <div className="msg-body"><span className="thinking">thinking</span></div>
        </div>
      )}

      <div ref={endRef} />
    </div>
  );
}

const REACTION_EMOJIS = ["👍", "👎", "❓"];

function Message({ message, channelId }: { message: SessionMessage; channelId: string }) {
  // System turns render as a thin inline notice — used today for
  // channel-state changes like "Brainstorm enabled". Not a chat
  // bubble; just a small line teammates can scan past.
  if (message.role === "system") {
    return <div className="msg-system">{message.content}</div>;
  }

  // User turns are rendered literally — what they typed is what they
  // see. Assistant turns go through markdown so code blocks, lists,
  // and links land properly.
  const renderMarkdown = message.role === "assistant";
  const authorLabel = message.author ?? message.role;
  // Stable per-user color for the author name. Assistant turns keep
  // the default --assistant orange (they're always the same
  // "person"); user turns get hashed colors so long multi-user
  // threads stay scannable at a glance.
  const authorStyle = message.role === "user"
    ? { color: colorForUser(authorLabel) }
    : undefined;
  return (
    <div className={`msg ${message.role}`}>
      <div className="msg-meta">
        <span className="author" style={authorStyle}>{authorLabel}</span>
        {message.picked_provider && <span>via {message.picked_provider}</span>}
        {message.model && !message.picked_provider && <span>{message.model}</span>}
        {message.tool_rounds && message.tool_rounds.length > 0 && (
          <span>{message.tool_rounds.length} tool call{message.tool_rounds.length === 1 ? "" : "s"}</span>
        )}
      </div>
      {message.tool_rounds && message.tool_rounds.length > 0 && (
        <ToolRounds rounds={message.tool_rounds} />
      )}
      <div className="msg-body">
        {renderMarkdown ? <MD>{message.content}</MD> : message.content}
      </div>
      <ReactionStrip channelId={channelId} message={message} />
    </div>
  );
}

function ReactionStrip({ channelId, message }: { channelId: string; message: SessionMessage }) {
  const toggleReaction = useSessionStore((s) => s.toggleReaction);
  const userName = useAuthStore((s) => s.user?.name ?? "anon");
  const reactions = message.reactions ?? {};
  const reactedEmojis = Object.keys(reactions);

  return (
    <div className="msg-reactions">
      {reactedEmojis.map((emoji) => {
        const users = reactions[emoji];
        const youReacted = users.includes(userName);
        return (
          <button
            key={emoji}
            className={`msg-react-chip ${youReacted ? "you" : ""}`}
            onClick={() => toggleReaction(channelId, message.id, emoji, userName)}
            title={users.join(", ")}
          >
            {emoji} <span className="msg-react-count">{users.length}</span>
          </button>
        );
      })}
      <span className="msg-react-add">
        {REACTION_EMOJIS.filter((e) => !reactedEmojis.includes(e)).map((emoji) => (
          <button
            key={emoji}
            className="msg-react-pick"
            onClick={() => toggleReaction(channelId, message.id, emoji, userName)}
            title={`react with ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </span>
    </div>
  );
}

/** Hash an author name to a stable hue, return an HSL color. Same
 *  name → same color across reloads, sessions, and devices. Sat and
 *  lightness are pinned so the colors all read as the same weight on
 *  the light-register background — only the hue differs. Avoids the
 *  narrow band around the user/assistant blues+oranges (--user blue,
 *  --assistant orange) by skipping hues 15-45 and 200-230. */
function colorForUser(name: string): string {
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h) ^ name.charCodeAt(i);
  }
  // Map to 0-359 then nudge out of the reserved bands.
  let hue = Math.abs(h) % 360;
  if (hue >= 15 && hue < 45) hue = (hue + 30) % 360;
  if (hue >= 200 && hue < 230) hue = (hue + 30) % 360;
  return `hsl(${hue}, 60%, 38%)`;
}

function ToolRounds({ rounds }: { rounds: NonNullable<SessionMessage["tool_rounds"]> }) {
  return (
    <div className="tool-rounds">
      {rounds.map((r, i) => (
        <ToolRoundCard key={i} round={r} />
      ))}
    </div>
  );
}

function ToolRoundCard({ round }: { round: NonNullable<SessionMessage["tool_rounds"]>[number] }) {
  const [open, setOpen] = useState(false);
  const argsSummary = summarizeArgs(round.args);
  return (
    <div className={`tool-round ${round.ok ? "ok" : "fail"}`}>
      <button className="tool-round-head" onClick={() => setOpen((v) => !v)}>
        <span className="tool-round-status">{round.ok ? "✓" : "✗"}</span>
        <span className="tool-round-name">{round.name}</span>
        <span className="tool-round-args">{argsSummary}</span>
        <span className="tool-round-toggle">{open ? "−" : "+"}</span>
      </button>
      {open && <pre className="tool-round-detail">{round.detail}</pre>}
    </div>
  );
}

function summarizeArgs(args: unknown): string {
  if (args === null || args === undefined) return "()";
  if (typeof args === "string") return `("${truncate(args, 60)}")`;
  if (typeof args !== "object") return `(${String(args)})`;
  const entries = Object.entries(args as Record<string, unknown>);
  if (entries.length === 0) return "()";
  const summary = entries
    .map(([k, v]) => {
      if (typeof v === "string") return `${k}: "${truncate(v, 40)}"`;
      return `${k}: ${truncate(JSON.stringify(v), 40)}`;
    })
    .join(", ");
  return `(${summary})`;
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}
